import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RefereeStats, RefereeTrend, StreakSummary } from '@proref/types';
import { Skeleton } from '../shared/Skeleton';
import { Icon } from '../shared/Icons';
import { AnimatedNumber } from '../shared/AnimatedNumber';
import { Sparkline } from './Sparkline';

// The streak/accuracy sparklines used to be a fixed, decorative series (see Sparkline's former
// note) — GET /v1/me/trend now supplies the real history. Activity is daily attempts (any attempt,
// repeats included — practising still counts as showing up); accuracy is weekly and drops weeks
// with no answers rather than plotting a fake 0%, same "gap, not a zero" rule the admin referee
// trend chart follows.
function activitySpark(trend: RefereeTrend | undefined): number[] {
  return trend?.activity.map((d) => d.attempts) ?? [];
}
function accuracySpark(trend: RefereeTrend | undefined): number[] {
  return (
    trend?.accuracy
      .filter((w) => w.accuracy != null)
      .map((w) => Math.round((w.accuracy as number) * 100)) ?? []
  );
}

// The four top-of-Home stat tiles, all backed by live data: the Study-streak tile reads
// GET /v1/me/streak (M9 daily loop), the other three GET /v1/me/stats + the referee clip count.

interface StatTileProps {
  label: string;
  // The big figure. ReactNode so a card can compose a value + unit (74 %) or a locked dash.
  value: ReactNode;
  caption?: ReactNode;
  // Optional footer slot for the accuracy footnote / progress bar / drill link.
  footer?: ReactNode;
  // Optional bottom-right micro-trend, sat on the caption row (matches the design's stat cards).
  chart?: ReactNode;
  // Dims the tile and drops the value opacity — used for the not-yet-live streak card.
  muted?: boolean;
}

function StatTile({ label, value, caption, footer, chart, muted }: StatTileProps) {
  return (
    <div className="relative overflow-hidden rounded border border-line bg-surface p-5 shadow-1">
      {/* Sparkline fills the right side of the tile, behind the figures — a large, low trend that
          rises into the corner (the design's stat-card anatomy). Non-interactive backdrop. */}
      {chart != null && <div className="pointer-events-none absolute inset-y-4 right-4 w-2/5">{chart}</div>}

      {/* Text content sits above the chart on the left. */}
      <div className="relative">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted">{label}</div>
        <div className={`mt-2 font-display text-4xl leading-none ${muted ? 'text-muted-2' : 'text-ink-3'}`}>
          {value}
        </div>
        {caption != null && <div className="mt-2 text-[11px] text-muted">{caption}</div>}
        {footer}
      </div>
    </div>
  );
}

interface HomeStatCardsProps {
  stats: RefereeStats | undefined;
  // Total published clips in the referee's library — the denominator for "clips reviewed". Real
  // (from useRefereeClipsQuery); undefined while loading.
  totalClips: number | undefined;
  loading: boolean;
  // The referee's streak (GET /v1/me/streak) + its own loading flag — separate query from stats.
  streak: StreakSummary | undefined;
  streakLoading: boolean;
  // Real history for the two sparklines (GET /v1/me/trend) — separate query, so it can arrive (or
  // fail) independently of stats/streak without blocking either card's headline figure.
  trend: RefereeTrend | undefined;
}

export function HomeStatCards({ stats, totalClips, loading, streak, streakLoading, trend }: HomeStatCardsProps) {
  const { t } = useTranslation();
  const streakDays = streak?.current_streak ?? 0;
  const accuracyPct = stats ? Math.round(stats.rule_accuracy * 100) : 0;
  const answered = stats?.clips_answered ?? 0;
  // Progress bar fill: answered / total published. Guard the divide so an empty library reads 0.
  const progress = totalClips && totalClips > 0 ? Math.min(1, answered / totalClips) : 0;
  const weakest = stats?.weakest_rule ?? null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {/* Study streak — live from the M9 daily loop: consecutive days the daily clip was answered. */}
      <StatTile
        label={t('home.statCards.studyStreak')}
        value={
          streakLoading ? (
            <Skeleton className="h-9 w-16" />
          ) : (
            <>
              <AnimatedNumber value={streakDays} />
              <span className="ml-1 align-baseline text-lg text-muted">
                {t('home.statCards.streakUnit', { count: streakDays })}
              </span>
            </>
          )
        }
        caption={
          streakLoading ? undefined : streak?.answered_today ? (
            <span className="inline-flex items-center gap-1.5 text-success">
              <Icon.check className="h-3 w-3" />
              {t('home.statCards.answeredToday')}
            </span>
          ) : streakDays > 0 ? (
            <span className="inline-flex items-center gap-1 text-ink-2">
              <span aria-hidden="true">↑</span> {t('home.statCards.keepItAlive')}
            </span>
          ) : (
            t('home.statCards.startToday')
          )
        }
        chart={streakLoading ? undefined : <Sparkline data={activitySpark(trend)} className="text-accent" />}
      />

      {/* Rule accuracy — live overall hit rate. The design's "+4% WoW" delta figure still isn't
          built (that needs a period-over-period comparison, not just a series), so the caption
          states the honest basis (across N answered clips) rather than a number we can't back. */}
      <StatTile
        label={t('home.statCards.ruleAccuracy')}
        value={
          loading ? (
            <Skeleton className="h-9 w-20" />
          ) : (
            <>
              <AnimatedNumber value={accuracyPct} />
              <span className="ml-1 align-baseline text-lg text-muted">%</span>
            </>
          )
        }
        caption={loading ? undefined : t('home.statCards.acrossAnswered', { count: answered })}
        chart={loading ? undefined : <Sparkline data={accuracySpark(trend)} className="text-accent" />}
      />

      {/* Clips reviewed — answered vs the published library size. Both numbers are real; the design's
          "season progress" framing is M9, so this is a plain answered/total completion bar. */}
      <StatTile
        label={t('home.statCards.clipsReviewed')}
        value={
          loading ? (
            <Skeleton className="h-9 w-24" />
          ) : (
            <>
              <AnimatedNumber value={answered} />
              {totalClips != null && (
                <span className="ml-1 align-baseline text-lg text-muted">/ {totalClips}</span>
              )}
            </>
          )
        }
        footer={
          loading || totalClips == null ? null : (
            <div className="mt-3">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-line-2">
                <div
                  className="h-full rounded-full bg-accent transition-all"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <div className="mt-1.5 text-[11px] text-muted">
                <AnimatedNumber value={Math.round(progress * 100)} />
                {t('home.statCards.percentOfLibrary')}
              </div>
            </div>
          )
        }
      />

      {/* Weakest area — fully live. Big rule number, accuracy footnote, and a Drill link into the
          library (the Recommended section below pulls clips from this same rule). */}
      <StatTile
        label={t('home.statCards.weakestArea')}
        value={
          loading ? (
            <Skeleton className="h-9 w-28" />
          ) : weakest ? (
            <span className="text-3xl">{t('common.ruleLabel', { number: weakest.article_number })}</span>
          ) : (
            <span className="text-2xl text-muted-2">—</span>
          )
        }
        footer={
          loading ? null : weakest ? (
            <div className="mt-2 text-[11px] text-muted">
              <AnimatedNumber value={Math.round(weakest.accuracy_score * 100)} />
              {t('home.statCards.weakestCorrectSuffix')} ·{' '}
              <Link to="/clips" className="font-medium text-accent underline underline-offset-2 hover:text-accent-2">
                {t('home.statCards.drill')}
              </Link>
            </div>
          ) : (
            <div className="mt-2 text-[11px] text-muted">{t('home.statCards.answerFewClips')}</div>
          )
        }
      />
    </div>
  );
}
